/**
 * Permission catalog & tenant permission groups (admin API).
 */

import type { ScreenRouteRule } from '@/config/screenPermissions';
import { SCREEN_RULES_EDIT_ENABLED } from '@/config/screenRulesFeature';
import { apiClient } from '@/services/api/client';

export interface EffectivePermissionsPayload {
    effective_permissions: string[];
    source: string;
}

export interface ScreenRulesApiWire {
    path_prefix: string;
    any_of: string[];
}

export interface ScreenRulesPayload {
    rules: ScreenRouteRule[];
}

export interface PermissionCatalogEntry {
    key: string;
    code: string;
}

export interface PermissionGroup {
    id: string;
    name: string;
    slug: string;
    description: string;
    permissions: string[];
    is_system: boolean;
    tenant_id: string | null;
    created_at: string | null;
    updated_at: string | null;
}

export interface PaginatedResults<T> {
    results: T[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
}

class PermissionsService {
    private basePath = '/permissions';

    private tenantPermissionGroupsPath(tenantId: string): string {
        return `/tenants/${tenantId}/permission-groups`;
    }

    async getEffectivePermissions(): Promise<EffectivePermissionsPayload> {
        const response = await apiClient.get<EffectivePermissionsPayload>(
            `${this.basePath}/effective/`
        );
        return response.data;
    }

    async getScreenRules(): Promise<ScreenRulesPayload> {
        const response = await apiClient.get<{ rules: ScreenRulesApiWire[] }>(
            `${this.basePath}/screen-rules/`
        );
        const raw = response.data.rules ?? [];
        const rules: ScreenRouteRule[] = raw.map((r) => ({
            pathPrefix: r.path_prefix,
            anyOf: r.any_of,
        }));
        return { rules };
    }

    /**
     * Persist screen rules (staff + dashboard.manage). Disabled unless `SCREEN_RULES_EDIT_ENABLED`
     * and the server enables PUT.
     */
    async putScreenRules(body: { rules: ScreenRouteRule[] }): Promise<ScreenRulesPayload> {
        if (!SCREEN_RULES_EDIT_ENABLED) {
            return Promise.reject(
                new Error('Screen rules editing is disabled (SCREEN_RULES_EDIT_ENABLED).')
            );
        }
        const wire = body.rules.map((r) => ({
            path_prefix: r.pathPrefix,
            any_of: [...r.anyOf],
        }));
        const response = await apiClient.put<{ rules: ScreenRulesApiWire[] }>(
            `${this.basePath}/screen-rules/`,
            { rules: wire }
        );
        const raw = response.data.rules ?? [];
        const rules: ScreenRouteRule[] = raw.map((r) => ({
            pathPrefix: r.path_prefix,
            anyOf: r.any_of,
        }));
        return { rules };
    }

    async getCatalog(): Promise<{ permissions: PermissionCatalogEntry[] }> {
        const response = await apiClient.get<{ permissions: PermissionCatalogEntry[] }>(
            `${this.basePath}/catalog/`
        );
        return response.data;
    }

    /**
     * List permission groups for a tenant (admin: staff JWT; path-scoped tenant).
     */
    async listGroups(params: {
        tenantId: string;
        page?: number;
        page_size?: number;
        search?: string;
        is_system?: 'true' | 'false';
    }): Promise<PaginatedResults<PermissionGroup>> {
        const q = new URLSearchParams();
        if (params.page) q.set('page', String(params.page));
        if (params.page_size) q.set('page_size', String(params.page_size));
        if (params.search) q.set('search', params.search);
        if (params.is_system) q.set('is_system', params.is_system);
        const qs = q.toString();
        const path = `${this.tenantPermissionGroupsPath(params.tenantId)}${qs ? `?${qs}` : ''}`;
        const response = await apiClient.get<PaginatedResults<PermissionGroup>>(path);
        return response.data;
    }

    async createGroup(
        tenantId: string,
        body: { name: string; slug: string; description?: string; permissions: string[] }
    ): Promise<PermissionGroup> {
        const response = await apiClient.post<PermissionGroup>(
            this.tenantPermissionGroupsPath(tenantId),
            body
        );
        return response.data;
    }

    async patchGroup(
        tenantId: string,
        groupId: string,
        body: Partial<{ name: string; description: string; permissions: string[] }>
    ): Promise<PermissionGroup> {
        const response = await apiClient.patch<PermissionGroup>(
            `${this.tenantPermissionGroupsPath(tenantId)}/${groupId}`,
            body
        );
        return response.data;
    }

    async deleteGroup(tenantId: string, groupId: string): Promise<void> {
        await apiClient.delete(`${this.tenantPermissionGroupsPath(tenantId)}/${groupId}`);
    }
}

export const permissionsService = new PermissionsService();
export default permissionsService;
